import { useQuery } from '@apollo/client';
import { GET_IMPACT_CLAIMS } from 'graphql/secure/queries';
import { useMemo } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import useConfig from '../../../hooks/useConfig';
import { IGraphQlConnection } from 'types/core.types';
import { ImpactClaim, ImpactClaimStatus } from 'FeatureModules/Request/types/impactClaim.types.js';
import { removeGraphConnections } from 'utils/graphql.utils';
import { IMPACT_CLAIMS_PER_PAGE } from 'utils/pagination.utils';

interface IImpactClaimHookProps {
  status?: ImpactClaimStatus[];
}

const useImpactClaims = ({ status }: IImpactClaimHookProps = {}) => {
  const { appQueryParams } = useConfig();
  const [searchTerm = '', setSearchTerm] = useQueryParam(appQueryParams.query, StringParam);

  const { data, loading, error, fetchMore } = useQuery<{
    impactClaims: IGraphQlConnection<ImpactClaim>;
  }>(GET_IMPACT_CLAIMS, {
    variables: {
      filters: {
        ...(status && status.length ? { status } : {}),
        ...(searchTerm ? { searchTerm } : {}),
      },
      first: IMPACT_CLAIMS_PER_PAGE,
    },
    fetchPolicy: 'cache-and-network',
  });

  const hasNextPage: boolean = !!data?.impactClaims?.pageInfo?.hasNextPage;
  const endCursor = data?.impactClaims?.pageInfo?.endCursor;

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  const handleSearchTermChange = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };

  const impactClaims = useMemo(
    () => (data?.impactClaims ? removeGraphConnections(data.impactClaims) : []),
    [data]
  );

  return {
    impactClaims,
    loading,
    error,
    hasNextPage,
    searchTerm,
    endCursor,
    handlePageEndReached,
    handleSearchTermChange,
  };
};

export default useImpactClaims;
